import { createInertiaApp, router } from "@inertiajs/react";
import { createElement, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ShowFlashProvider } from "@sprout/flash.tsx";
import Layout from "@components/layout";
import axios from "axios";
import { InertiaErrorBoundary } from "@/errors/inertia_error_boundary.tsx";

const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

// Set the csrfToken for non-inertia requests
if (csrfToken) {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
}

router.on("success", (event) => {
  const newCsrfToken = event.detail.page.props.csrfToken as string | undefined;
  // keep the csrf token in axios up to date
  if (newCsrfToken) {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = newCsrfToken;
  }
});

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode;
  layout?: (page: ReactNode) => ReactNode;
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.netlify.app/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar for now because it doesn't work with CSP unsafe-inline
  // https://github.com/inertiajs/progress/issues/21
  progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // @ts-expect-error Inertia pages have a layout property
    page.default.layout =
      // @ts-expect-error Inertia pages have a layout property
      page.default.layout || ((content: ReactNode) => <Layout>{content}</Layout>);
    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(
          ShowFlashProvider,
          null,
          createElement(InertiaErrorBoundary, null, createElement(App, props)),
        ),
      );
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
